import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Chip,
	IconButton,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import {
	DISPLAY_DATE_FORMAT,
	DISPLAY_DATE_TIME_FORMAT,
} from '@/utils/dateHelper';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import { useDeleteFaceSheetMutation } from '@/api/forms-management/faceSheet';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import {
	FormManagementStatus,
	FormManagementType,
	FormManagementTypeEnum,
	PermissionEnum,
	PreAdmissionTab,
} from '@/api/enum/common.enum';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import { ReactElement } from 'react';
import { useDeleteEnquiryRecordMutation } from '@/api/forms-management/enquiryRecord';
import { useDeleteAppendix6Mutation } from '@/api/forms-management/appendix6';
import { useDeleteAppendix5Mutation } from '@/api/forms-management/appendix5';
import { useDeleteAppendix4Mutation } from '@/api/forms-management/appendix4';
import FormManagementChip from '@/common/components/FormManagementChip';
import { useDeleteAppendix7Mutation } from '@/api/forms-management/appendix7';
import { useDeleteMemberApplicationMutation } from '@/api/forms-management/memberApplication';
import { useDeleteNewConsentMutation } from '@/api/forms-management/newConsent';
import { useDeleteStopServiceMutation } from '@/api/forms-management/stopService';
import { useDeleteWellBeingScaleMutation } from '@/api/forms-management/wellBeingScale';
import { useDeleteRecoveryAssessmentScaleMutation } from '@/api/forms-management/recoveryAssessmentScale';
import { useDeleteDsm5AdultMutation } from '@/api/forms-management/dsm5Adult';
import { useDeleteDsm5TeenMutation } from '@/api/forms-management/dsm5Teen';
import {
	useDeleteUploadFormMutation,
	useGetAppendix4GenerateReportMutation,
	useGetAppendix5GenerateReportMutation,
	useGetAppendix6GenerateReportMutation,
	useGetAppendix7GenerateReportMutation,
	useGetConsentPersonalDataUsageGenerateReportMutation,
	useGetConsentServiceTerminationGenerateReportMutation,
	useGetDsmFiveAdultGenerateReportMutation,
	useGetDsmFiveTeenGenerateReportMutation,
	useGetEnquiryRecordGenerateReportMutation,
	useGetExportCustomerPDFMutation,
	useGetFaceSheetGenerateReportMutation,
	useGetMemberApplicationGenerateReportMutation,
	useGetRecoveryAssessmentScaleGenerateReportMutation,
	useGetWellBeingScaleGenerateReportMutation,
} from '@/api/common';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useGetUser } from '@/hooks/useGetUser';

interface Props {
	expanded: boolean;
	type: string;
	buttonElement: ReactElement;
	tableList: any;
	title: string;
	setExpanded: (value: boolean) => void;
}
export default function FormListContainer(props: Props) {
	const { expanded, tableList, type, buttonElement, setExpanded, title } =
		props;
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const memberListType = location.pathname.includes('admission-request-list')
		? 'admission-request-list'
		: 'member-list';
	const { preAdmissionId, admissionRequestId } = useParams();
	const showModal = useModal();
	const [deleteUploadForm] = useDeleteUploadFormMutation();

	const [deleteFaceSheet] = useDeleteFaceSheetMutation();
	const [deleteEnquiryRecord] = useDeleteEnquiryRecordMutation();
	const [deleteAppendix6] = useDeleteAppendix6Mutation();
	const [deleteAppendix5] = useDeleteAppendix5Mutation();
	const [deleteAppendix4] = useDeleteAppendix4Mutation();
	const [deleteAppendix7] = useDeleteAppendix7Mutation();
	const [deleteMemberApplication] = useDeleteMemberApplicationMutation();
	const [deleteNewConsent] = useDeleteNewConsentMutation();
	const [deleteStopService] = useDeleteStopServiceMutation();
	const [deleteWellBeingScale] = useDeleteWellBeingScaleMutation();
	const [deleteRecoveryAssessmentScale] =
		useDeleteRecoveryAssessmentScaleMutation();
	const [deleteDsm5Adult] = useDeleteDsm5AdultMutation();
	const [deleteDsm5Teen] = useDeleteDsm5TeenMutation();
	const [getExportCustomerPDF] = useGetExportCustomerPDFMutation();
	const [getFaceSheetGenerateReport] = useGetFaceSheetGenerateReportMutation();
	const [getEnquiryRecordGenerateReport] =
		useGetEnquiryRecordGenerateReportMutation();
	const [getAppendix4GenerateReport] = useGetAppendix4GenerateReportMutation();
	const [getAppendix5GenerateReport] = useGetAppendix5GenerateReportMutation();
	const [getAppendix6GenerateReport] = useGetAppendix6GenerateReportMutation();
	const [getMemberApplicationGenerateReport] =
		useGetMemberApplicationGenerateReportMutation();
	const [getConsentPersonalDataUsageGenerateReport] =
		useGetConsentPersonalDataUsageGenerateReportMutation();
	const [getConsentServiceTerminationGenerateReport] =
		useGetConsentServiceTerminationGenerateReportMutation();
	const [getAppendix7GenerateReport] = useGetAppendix7GenerateReportMutation();
	const [getWellBeingScaleGenerateReport] =
		useGetWellBeingScaleGenerateReportMutation();
	const [getRecoveryAssessmentScaleGenerateReport] =
		useGetRecoveryAssessmentScaleGenerateReportMutation();
	const [getDsmFiveAdultGenerateReport] =
		useGetDsmFiveAdultGenerateReportMutation();
	const [getDsmFiveTeenGenerateReport] =
		useGetDsmFiveTeenGenerateReportMutation();
	const { userInfo } = useGetUser();

	const columns: GridColDef[] = [
		{
			field: 'versionNo',
			headerName: t('preAdmission.version'),
			flex: 25,
		},
		{
			field: 'formStatusDesc',
			headerName: t('preAdmission.formStatus'),
			flex: 25,
			renderCell: (params: any) => (
				<FormManagementChip
					status={params.row.formStatus}
					label={params.row.formStatusDesc}
				/>
			),
		},
		{
			field: 'fileName',
			headerName: t('preAdmission.fileName'),
			flex: 25,
		},
		{
			field: 'description',
			headerName: t('preAdmission.description'),
			flex: 25,
		},
		{
			field: 'createdAt',
			headerName: t('preAdmission.uploadedDate'),
			flex: 25,
			renderCell: (params: any) =>
				!params.row.createdAt
					? ''
					: dayjs(params.row.createdAt)
							.locale('en')
							.format(DISPLAY_DATE_FORMAT),
		},
		{
			field: 'updatedAt',
			headerName: t('preAdmission.modifiedDateTime'),
			flex: 25,
			renderCell: (params: any) =>
				!params.row.updatedAt
					? ''
					: dayjs(params.row.updatedAt)
							.locale('en')
							.format(DISPLAY_DATE_TIME_FORMAT),
		},
		{
			field: 'actionedBy',
			headerName: t('preAdmission.actionedBy'),
			flex: 25,
		},
		{
			field: 'edit',
			headerName: '',
			width: 1,
			renderCell: (params: any) =>
				((params.row.formStatus == FormManagementStatus.submitted &&
					userInfo?.permissionList?.includes(
						PermissionEnum.ViewSubmittedOrUploadedForm
					)) ||
					(params.row.formStatus == FormManagementStatus.draft &&
						userInfo?.permissionList?.includes(
							PermissionEnum.EditDraftForm
						))) &&
				(params.row.formStatus == FormManagementStatus.submitted ||
					params.row.formStatus == FormManagementStatus.draft) && (
					<Link
						to={
							params.row.formStatus == FormManagementStatus.submitted
								? preAdmissionId
									? `/pre-admission/pre-admission-list/detail/${
											preAdmissionId || admissionRequestId
										}/forms-management/detail-${type}/${params.id}`
									: `/member-management/${memberListType}/detail/${
											preAdmissionId || admissionRequestId
										}/forms-management/detail-${type}/${params.id}`
								: preAdmissionId
									? `/pre-admission/pre-admission-list/detail/${
											preAdmissionId || admissionRequestId
										}/forms-management/edit-${type}/${params.id}`
									: `/member-management/${memberListType}/detail/${
											preAdmissionId || admissionRequestId
										}/forms-management/edit-${type}/${params.id}`
						}
					>
						<IconButton color='secondary'>
							{params.row.formStatus == FormManagementStatus.submitted ? (
								<VisibilityOutlinedIcon />
							) : params.row.formStatus == FormManagementStatus.draft ? (
								<CreateOutlinedIcon />
							) : (
								''
							)}
						</IconButton>
					</Link>
				),
		},
		{
			field: 'downLoad',
			headerName: '',
			width: 1,
			renderCell: (params: any) =>
				userInfo?.permissionList?.includes(PermissionEnum.DownloadForm) && (
					<IconButton
						color='secondary'
						onClick={async () => {
							console.log(params.row);
							if (
								params.row.formStatus == FormManagementStatus.submitted ||
								params.row.formStatus == FormManagementStatus.draft
							) {
								let response: any = {};
								if (
									params.row.formType ==
									FormManagementTypeEnum[FormManagementType.faceSheet]
								) {
									response = await getFaceSheetGenerateReport(params.row.id);
								}
								if (
									params.row.formType ==
									FormManagementTypeEnum[FormManagementType.enquiryRecord]
								) {
									response = await getEnquiryRecordGenerateReport(
										params.row.id
									);
								}
								if (
									params.row.formType ==
									FormManagementTypeEnum[FormManagementType.appendix4]
								) {
									response = await getAppendix4GenerateReport(params.row.id);
								}
								if (
									params.row.formType ==
									FormManagementTypeEnum[FormManagementType.appendix5]
								) {
									response = await getAppendix5GenerateReport(params.row.id);
								}
								if (
									params.row.formType ==
									FormManagementTypeEnum[FormManagementType.appendix6]
								) {
									response = await getAppendix6GenerateReport(params.row.id);
								}
								if (
									params.row.formType ==
									FormManagementTypeEnum[FormManagementType.memberApplication]
								) {
									response = await getMemberApplicationGenerateReport(
										params.row.id
									);
								}
								if (
									params.row.formType ==
									FormManagementTypeEnum[FormManagementType.newConsent]
								) {
									response = await getConsentPersonalDataUsageGenerateReport(
										params.row.id
									);
								}
								if (
									params.row.formType ==
									FormManagementTypeEnum[FormManagementType.stopService]
								) {
									response = await getConsentServiceTerminationGenerateReport(
										params.row.id
									);
								}
								if (
									params.row.formType ==
									FormManagementTypeEnum[FormManagementType.appendix7]
								) {
									console.log(123);
									response = await getAppendix7GenerateReport(params.row.id);
								}
								if (
									params.row.formType ==
									FormManagementTypeEnum[FormManagementType.wellBeingScale]
								) {
									response = await getWellBeingScaleGenerateReport(
										params.row.id
									);
								}
								if (
									params.row.formType ==
									FormManagementTypeEnum[
										FormManagementType.recoveryAssessmentScale
									]
								) {
									response = await getRecoveryAssessmentScaleGenerateReport(
										params.row.id
									);
								}
								if (
									params.row.formType ==
									FormManagementTypeEnum[FormManagementType.dsm5Adult]
								) {
									response = await getDsmFiveAdultGenerateReport(params.row.id);
								}
								if (
									params.row.formType ==
									FormManagementTypeEnum[FormManagementType.dsm5Teen]
								) {
									response = await getDsmFiveTeenGenerateReport(params.row.id);
								}

								if ('data' in response) {
									getExportCustomerPDF(response?.data?.data.url);
								}
							} else {
								getExportCustomerPDF(params.row.filePath);
							}
						}}
					>
						<FileDownloadOutlinedIcon />
					</IconButton>
				),
		},
		{
			field: 'delete',
			headerName: '',
			width: 1,
			renderCell: (params: any) =>
				((params.row.formStatus == FormManagementStatus.draft &&
					userInfo?.permissionList?.includes(PermissionEnum.DeleteDraftForm)) ||
					((params.row.formStatus == FormManagementStatus.submitted ||
						params.row.formStatus == FormManagementStatus.uploaded) &&
						userInfo?.permissionList?.includes(
							PermissionEnum.DeleteSubmittedOrUploadedForm
						))) && (
					<IconButton
						color='secondary'
						onClick={() => {
							handleDelete(params.row.id, params.row);
						}}
					>
						<DeleteOutlinedIcon />
					</IconButton>
				),
		},
	];

	const handleDelete = async (id: string, row: any) => {
		const deleteApiMap: Record<string, (id: string) => Promise<any>> = {
			[FormManagementType.appendix4]: deleteAppendix4,
			[FormManagementType.appendix5]: deleteAppendix5,
			[FormManagementType.appendix6]: deleteAppendix6,
			[FormManagementType.appendix7]: deleteAppendix7,
			[FormManagementType.faceSheet]: deleteFaceSheet,
			[FormManagementType.enquiryRecord]: deleteEnquiryRecord,
			[FormManagementType.memberApplication]: deleteMemberApplication,
			[FormManagementType.newConsent]: deleteNewConsent,
			[FormManagementType.stopService]: deleteStopService,
			[FormManagementType.wellBeingScale]: deleteWellBeingScale,
			[FormManagementType.recoveryAssessmentScale]:
				deleteRecoveryAssessmentScale,
			[FormManagementType.dsm5Adult]: deleteDsm5Adult,
			[FormManagementType.dsm5Teen]: deleteDsm5Teen,
		};

		const deleteApi = deleteApiMap[type];

		if (row.formStatus == FormManagementStatus.uploaded) {
			showModal((props: any) => (
				<ConfirmationDialog
					{...props}
					showCloseIcon={false}
					title={t('preAdmission.deleteConfirmation')}
					titleProps={{ variant: 'h6' }}
					description={
						<Typography variant='body1'>
							{t('preAdmission.isDeleteForm', { formName: title })}
						</Typography>
					}
					cancellationText={t('button.cancel')}
					confirmationText={t('button.confirm')}
					confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
					onConfirm={async () => {
						const response = await deleteUploadForm(id);
						if ('data' in response) {
							enqueueSnackbar(t('snackbar.deleteSuccess'), {
								variant: 'warning',
								anchorOrigin: { horizontal: 'center', vertical: 'top' },
								autoHideDuration: 3000,
								action: (key) => {
									return <CloseIcon onClick={() => closeSnackbar(key)} />;
								},
							});
						}
					}}
				/>
			));
			return;
		}

		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={false}
				title={t('preAdmission.deleteConfirmation')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('preAdmission.isDeleteForm', { formName: title })}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteApi(id);
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteSuccess'), {
							variant: 'warning',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
					}
				}}
			/>
		));
	};

	return (
		<>
			<Accordion
				expanded={expanded}
				onChange={() => setExpanded(!expanded)}
				sx={{
					border: '1px solid #D7D7D7',
					borderRadius: '16px!important',
					'&::before': {
						backgroundColor: 'transparent !important',
					},
					marginBottom: '24px',
					padding: '8px',
				}}
			>
				<AccordionSummary expandIcon={<ExpandMoreIcon />}>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							width: '100%',
						}}
					>
						<Typography
							sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}
						>
							{title}
							{tableList?.data.length > 0 && (
								<span
									style={{
										marginLeft: '8px',
										display: 'inline-block',
										backgroundColor: '#646D11',
										color: 'white',
										borderRadius: '50%',
										minWidth: '16px',
										height: '16px',
										maxWidth: '34px',
										textAlign: 'center',
										fontSize: '12px',
									}}
								>
									{tableList?.data.length || 0}
								</span>
							)}
						</Typography>

						<Box marginRight={2}>{buttonElement}</Box>
					</Box>
				</AccordionSummary>
				<AccordionDetails>
					<LocalDataGrid
						rows={tableList?.data ?? []}
						columns={columns}
						getRowId={(row) => row.id}
						pagination={false}
					/>
				</AccordionDetails>
			</Accordion>
		</>
	);
}
